// covered-by: frontends/carrie/src/plugins/__tests__/rehype-claim-citation.test.ts

/**
 * Turns the claim a review comment QUOTES into the link to the rule it rests on.
 *
 * The citation was a chip under the comment, so a reader had to match prose
 * against a chip themselves -- and in the ask-v2 review pane the chip does not
 * render at all, which leaves a finding asserting a registered claim with no
 * way to see it. The claim wording is already in the sentence; making that the
 * link is the smallest thing that can be clicked.
 *
 * why a rehype plugin and not a string replace before render: the body is
 * markdown, and a link spliced into the SOURCE would have to survive being
 * parsed -- a claim containing `*`, `_`, `[` or a backtick would be read as
 * emphasis, or would break the `**Fix:**` bold it usually sits inside. Splicing
 * into the hast AFTER parsing edits the rendered text nodes only, so markdown
 * syntax is already resolved and cannot be disturbed. It is also the pattern
 * this repo already uses for exactly this shape (`rehype-iconize-emoji`,
 * `rehype-color-chip`).
 *
 * why matching is best-effort: nothing guarantees the quote is in the prose.
 * christo composes `description` from the finding's own observation and
 * required_action (`warholReview.ts`), and the claim appears there only because
 * warhol's brief asks the verifier to name a rule by what it SAYS. So an
 * unmatched citation is an ordinary outcome, not a failure -- the comment
 * renders exactly as it does today and the chip remains the fallback.
 */

import { visit } from "unist-util-visit";

/** Custom element the Markdown component maps to the link renderer. */
export const CLAIM_CITATION_TAG = "claim-citation";

/** The minimum a claim must be before matching it is meaningful rather than noisy. */
export const MIN_LINKABLE_CHARS = 12;

export interface LinkableClaim {
  /** The registry rule, and the link's target. */
  ruleId: string;
  /** The source the rule was read from, carried so the viewer can open it. */
  sourceId?: string;
  /** What a deep link calls the cited citation: a cell range, or a page. */
  cite?: string;
  /**
   * 1-based position in the message's citation list.
   *
   * The chips under a message are numbered by that same position
   * (`ChatMessage.tsx`: `const n = index + 1`). Carrying it here is what lets
   * an inline link and a chip say "these are the same source" -- without it a
   * reader sees twelve numbered chips and three underlined phrases with no
   * way to tell which chip is which phrase.
   */
  index?: number;
  /** The claim as the registry holds it. Preferred over `quote` for matching. */
  statement?: string;
  /** The verbatim source cell, used when there is no statement. */
  quote?: string;
  /**
   * The prior chat that discussed this claim, when the graph recorded one.
   *
   * A claim can be linkable for two different reasons, and a reader needs to
   * know which: the registry GOVERNS it, or this enterprise already TALKED
   * about it. When this is set the link opens that conversation instead of the
   * registry row, and renders in the provenance colour.
   */
  chatId?: string;
  /** That conversation's title, for the link's tooltip. */
  chatLabel?: string;
  /** The memory that recorded this claim. Independent of `chatId`: a memory
   * and the conversation it came up in routinely land on the same claim, and
   * both are worth offering. */
  memoryId?: string;
  /** The memory's opening words, for the link's tooltip and its search. */
  memoryLabel?: string;
}

/** Longest first, so an overlapping shorter claim never wins the span. */
interface Needle {
  claim: LinkableClaim;
  text: string;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * The comparable form of a claim and of the prose that may contain it.
 *
 * Only characters that never change WHICH claim is meant are folded, and the
 * folding must be LENGTH-PRESERVING: the match is mapped back onto the original
 * text to build the link, so an index in the folded string has to be the same
 * index in the original. That rules out collapsing whitespace here, which is
 * why the needle is matched with a whitespace-tolerant pattern instead.
 *
 * why the case fold is per code point and not one `.toLowerCase()` on the whole
 * string: lowercasing is not length-preserving in general. U+0130 (the Turkish
 * dotted capital I, ordinary in consumer-brand artwork copy) lowercases to `i` +
 * U+0307 -- two UTF-16 units out of one -- and a single one of those anywhere
 * before or inside a match shifts every later offset, so the plugin would slice
 * the ORIGINAL text at the wrong index and link the wrong characters. Folding
 * character by character and keeping the original unit whenever the lowercase
 * form changes width makes the invariant true rather than merely asserted; such
 * a character simply does not case-fold, which at worst misses a match.
 * `for...of` walks code points, so an astral character keeps its 2-unit width
 * and the width check stays meaningful.
 */
function fold(value: string): string {
  const normalized = value.replace(/[‘’]/g, "'").replace(/[“”]/g, '"');
  let out = "";
  for (const ch of normalized) {
    const lower = ch.toLowerCase();
    out += lower.length === ch.length ? lower : ch;
  }
  return out;
}

/**
 * A pattern that finds `text` however the prose spaced or punctuated it.
 *
 * Runs of whitespace match any run, because a claim broken across a line in the
 * source is one space in the prose; and the trailing `*` a registry keeps on a
 * qualified claim is optional, because the pack does not always print it.
 */
function needlePattern(text: string): RegExp {
  const body = text
    .trim()
    .replace(/[*\s]+$/, "")
    .split(/\s+/)
    .map(escapeRegExp)
    .join("\\s+");
  return new RegExp(`${body}\\*?`, "g");
}

function needles(claims: LinkableClaim[]): Needle[] {
  const seen = new Set<string>();
  const out: Needle[] = [];
  for (const claim of claims) {
    if (!claim?.ruleId) continue;
    const text = (claim.statement || claim.quote || "").trim();
    const folded = fold(text).replace(/[*\s]+$/, "");
    if (folded.length < MIN_LINKABLE_CHARS || seen.has(folded)) continue;
    seen.add(folded);
    out.push({ claim, text });
  }
  // Longest first: when one claim's wording contains another's, the specific
  // one must claim the span before the general one takes half of it.
  return out.sort((a, b) => b.text.length - a.text.length);
}

interface Hit {
  start: number;
  end: number;
  claim: LinkableClaim;
}

/**
 * Where each claim sits in `text`, first occurrence only, without overlaps.
 *
 * Exported for the tests: the matching is the risky half of this feature, and
 * it is worth pinning without a renderer in the way.
 */
export function findClaimHits(text: string, claims: LinkableClaim[]): Hit[] {
  const haystack = fold(text);
  const hits: Hit[] = [];
  for (const { claim, text: needle } of needles(claims)) {
    const pattern = needlePattern(fold(needle));
    let match: RegExpExecArray | null;
    while ((match = pattern.exec(haystack)) !== null) {
      if (match[0].length === 0) break;
      const start = match.index;
      const end = start + match[0].length;
      // One link per claim, and never inside another claim's link.
      if (hits.some((hit) => start < hit.end && end > hit.start)) continue;
      hits.push({ start, end, claim });
      break;
    }
  }
  return hits.sort((a, b) => a.start - b.start);
}

export interface ClaimCitationOptions {
  claims: LinkableClaim[];
  /**
   * Disambiguator for the rendered testids, normally the id of the comment or
   * message this body belongs to. A review renders one body per comment in a
   * single pass, so two findings resting on the SAME registry rule would
   * otherwise emit the same `claim-citation-link-<ruleId>` twice on one page --
   * a Playwright strict-mode violation and a breach of the testid-uniqueness
   * rule. Optional: a body rendered alone needs no suffix.
   */
  scope?: string;
}

/** 1 for a rule's first link in a body, 2 for the next, and so on. */
function nextOccurrence(seen: Map<string, number>, ruleId: string): number {
  const next = (seen.get(ruleId) ?? 0) + 1;
  seen.set(ruleId, next);
  return next;
}

export default function rehypeClaimCitation(options: ClaimCitationOptions) {
  const claims = options?.claims ?? [];
  const scope = options?.scope ?? "";
  return (tree: unknown) => {
    if (claims.length === 0) return;
    // How many times each rule has been linked in THIS body. `scope` keeps two
    // comments apart; this keeps two mentions inside one comment apart, which
    // the prose can legitimately contain. Counted here because this is where
    // the duplication is created -- the renderer only sees one hit at a time.
    // The first stays bare so the common single-mention id reads as just the
    // rule id.
    const seen = new Map<string, number>();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    visit(tree as any, "text", function (node: any, index, parent: any) {
      if (!parent || index === null || index === undefined) return;
      // Never link inside an existing anchor, and never re-enter our own output.
      if (parent.tagName === "a" || parent.tagName === CLAIM_CITATION_TAG)
        return;
      if (node.data?.processedByClaimCitation) return;

      const text: string = typeof node.value === "string" ? node.value : "";
      const hits = findClaimHits(text, claims);
      if (hits.length === 0) return;

      const children: unknown[] = [];
      let cursor = 0;
      for (const hit of hits) {
        if (hit.start > cursor) {
          children.push({
            type: "text",
            value: text.slice(cursor, hit.start),
            data: { processedByClaimCitation: true },
          });
        }
        children.push({
          type: "element",
          tagName: CLAIM_CITATION_TAG,
          properties: {
            ruleId: hit.claim.ruleId,
            sourceId: hit.claim.sourceId ?? "",
            cite: hit.claim.cite ?? "",
            // Provenance rides along so the renderer can choose the target and
            // the colour without re-deriving it from the citation list.
            chatId: hit.claim.chatId ?? "",
            chatLabel: hit.claim.chatLabel ?? "",
            memoryId: hit.claim.memoryId ?? "",
            memoryLabel: hit.claim.memoryLabel ?? "",
            index: hit.claim.index ?? 0,
            scope,
            occurrence: nextOccurrence(seen, hit.claim.ruleId),
          },
          children: [
            {
              type: "text",
              // The pack's own wording, not the registry's: the sentence must
              // read as it was written.
              value: text.slice(hit.start, hit.end),
              data: { processedByClaimCitation: true },
            },
          ],
        });
        cursor = hit.end;
      }
      if (cursor < text.length) {
        children.push({
          type: "text",
          value: text.slice(cursor),
          data: { processedByClaimCitation: true },
        });
      }
      parent.children.splice(index, 1, ...children);
      // Skip the nodes just inserted, or the visitor walks into them.
      return index + children.length;
    });
  };
}
